import {
 useState
} from 'react';
import {
 Label
} from 'shared/components/ui/label';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 ChevronDown, ChevronUp, Plus, Trash2
} from 'lucide-react';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 JsonOpcodeStep, StepKind
} from 'builders/builderOpcode/jsonMode/types';
import {
 buildV2Step
} from '../V2StepBuilder';
import {
 V2_STEP_TYPES
} from '../V2OpCodeBuilderContext';
import dispatchInlineStep from 'builders/destudio/opcode-editor/inline-steps/dispatchInlineStep';
import {
 buildOelContext
} from 'builders/destudio/primitives/oel';

const DefinitionsEditor = ({
    definitions,
    onChange,
    inputs = [],
    steps = [],
}: {
    definitions: JsonOpcodeStep[];
    onChange: (definitions: JsonOpcodeStep[]) => void;
    inputs?: string[];
    steps?: JsonOpcodeStep[];
}) => {
    const [isOpen, setIsOpen] = useState(definitions.length > 0);
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

    const handleAdd = () => {
        const newDef = buildV2Step('llm', definitions);
        // Give it a meaningful def_ prefix
        newDef.id = `def_${definitions.length + 1}`;
        onChange([...definitions, newDef]);
        setExpandedIndex(definitions.length);
        setIsOpen(true);
    };

    const handleRemove = (index: number) => {
        onChange(definitions.filter((_, i) => i !== index));
        if (expandedIndex === index) setExpandedIndex(null);
    };

    const handleStepChange = (index: number, step: JsonOpcodeStep) => {
        onChange(definitions.map((d, i) => i === index ? step : d));
    };

    const handleIdChange = (index: number, newId: string) => {
        onChange(definitions.map((d, i) => i === index ? {
 ...d,
id: newId
} : d));
    };

    const handleKindChange = (index: number, kind: StepKind) => {
        const existing = definitions[index];
        const newStep = buildV2Step(kind, definitions);
        onChange(definitions.map((d, i) =>
            i === index ? {
 ...newStep,
id: existing.id
} : d
        ));
    };

    return (
        <div className="border rounded-md">
            <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full flex justify-between items-center px-3 py-2"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="text-sm font-medium">
                    Definitions ({definitions.length})
                </span>
                {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>

            {isOpen && (
                <div className="p-3 space-y-3 border-t">
                    <p className="text-xs text-muted-foreground">
                        Reusable step definitions that can be referenced by Choose steps.
                    </p>

                    {definitions.map((def, index) => (
                        // Key uses index — not def.id — because def.id is the
                        // editable identifier; if we keyed on it, every
                        // keystroke would remount the row and steal focus.
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={index} className="border rounded-md">
                            <div
                                className="flex items-center gap-2 p-2 cursor-pointer hover:bg-accent/50"
                                onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                            >
                                <span className="text-sm font-mono font-semibold flex-1">{def.id}</span>
                                <span className="text-xs text-muted-foreground">{def.kind}</span>
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); handleRemove(index); }}
                                >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                                {expandedIndex === index ? (
                                    <ChevronUp className="h-4 w-4" />
                                ) : (
                                    <ChevronDown className="h-4 w-4" />
                                )}
                            </div>

                            {expandedIndex === index && (
                                <div className="p-3 space-y-3 border-t">
                                    <div className="flex gap-2">
                                        <div className="flex-1 space-y-1">
                                            <Label className="text-xs">Definition ID</Label>
                                            <Input
                                                value={def.id}
                                                onChange={(e) => handleIdChange(index, e.target.value)}
                                                placeholder="def_name"
                                                className="font-mono text-sm"
                                            />
                                        </div>
                                        <div className="flex-1 space-y-1">
                                            <Label className="text-xs">Step Kind</Label>
                                            <Select
                                                value={def.kind}
                                                onValueChange={(val) => handleKindChange(index, val as StepKind)}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {V2_STEP_TYPES.map((t) => (
                                                        <SelectItem key={t.name} value={t.name}>{t.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    {/* Render with the destudio inline-step editors so a definition
                                        looks identical to a top-level step (StepCommonAdvanced,
                                        kind-color cards, OelEditor, etc.). */}
                                    {dispatchInlineStep({
                                        step: def,
                                        onPatchStep: (updated: JsonOpcodeStep) => handleStepChange(index, updated),
                                        definitions,
                                        flows: [],
                                        oelContext: buildOelContext({
                                            inputs: inputs.map((n) => ({
 name: n,
type: 'string'
})),
                                            steps: steps || [],
                                            definitions,
                                            flows: [],
                                            currentStepIdx: null
                                        })
                                    })}
                                </div>
                            )}
                        </div>
                    ))}

                    <Button type="button" size="sm" variant="outline" onClick={handleAdd} className="w-full">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Definition
                    </Button>
                </div>
            )}
        </div>
    );
};

export default DefinitionsEditor;
